/**
 * @author       yontaek@zumper.com (Yon Taek Chung)
 * @copyright    Copyright (c) 2022, Zumper
 */

import type { AutocompleteApiResult } from '@zcode/bizos'
import { noop } from '@zcode/utils'
import { type Dispatch, type SetStateAction, createContext, useContext } from 'react'
import invariant from 'tiny-invariant'

export interface AutocompleteContextProps {
  forceContextualMenuOpen: boolean
  hoveredIndex: number
  isFocused: boolean
  keystrokes: number
  lat: number | null
  lng: number | null
  predictionResults: readonly AutocompleteApiResult[] | null
  prevSearchTerm: string
  searchTerm: string
  setForceContextualMenuOpen: Dispatch<SetStateAction<boolean>>
  setHoveredIndex: Dispatch<SetStateAction<number>>
  setIsFocused: Dispatch<SetStateAction<boolean>>
  setKeystrokes: Dispatch<SetStateAction<number>>
  setLat: Dispatch<SetStateAction<number | null>>
  setLng: Dispatch<SetStateAction<number | null>>
  setPredictionResults: Dispatch<SetStateAction<readonly AutocompleteApiResult[] | null>>
  setPrevSearchTerm: Dispatch<SetStateAction<string>>
  setSearchTerm: Dispatch<SetStateAction<string>>
}

export const AutocompleteContext = createContext<AutocompleteContextProps>({
  forceContextualMenuOpen: false,
  hoveredIndex: -1,
  isFocused: false,
  keystrokes: 0,
  lat: null,
  lng: null,
  predictionResults: null,
  prevSearchTerm: '',
  searchTerm: '',
  setForceContextualMenuOpen: noop,
  setHoveredIndex: noop,
  setIsFocused: noop,
  setKeystrokes: noop,
  setLat: noop,
  setLng: noop,
  setPredictionResults: noop,
  setPrevSearchTerm: noop,
  setSearchTerm: noop,
})

export const useAutocompleteContext = () => {
  const context = useContext(AutocompleteContext)
  invariant(context)
  return context
}
